import * as Phaser from "phaser-ce"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { DebugPanel } from "../../../generic-sprites/DebugPanel"
import { ExpandoSprite } from "../../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { TobaccoTourActivityScreen } from "../sprites/TobaccoTourActivityScreen"
import { loadAssets, TtAtlas, TtAudio, TtImage } from "../TobaccoTourAssets"
import {
    TobaccoTourProgressStore,
    TobaccoTourStages,
} from "../TobaccoTourProgressStore"
import {
    createTobaccoTourMolecule,
    TobaccoTourMolecule,
    TobaccoTourMoleculeTypes,
} from "../TobaccoTourTypes"
import { SmokelessTourChewAnimation } from "./SmokelessTourChewAnimation"
import { SmokelessTourSnuffAnimation } from "./SmokelessTourSnuffAnimation"
import { SmokelessTourStages } from "./SmokelessTourTypes"

export class ChewTourState extends Phaser.State {
    snuff: ExpandoSprite
    chew: ExpandoSprite
    skull: ExpandoSprite

    smokelessParts: Phaser.Group
    private stages: Array<SmokelessTourStages> = []

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)

        Sprites.add(this.game, TtImage.MICROSCOPE_SLIDE, {
            x: Scale.x(0.159),
            y: Scale.y(0.335),
            group: this.game.add.group(),
        })

        this.smokelessParts = this.game.add.group()

        this.skull = new ExpandoSprite(
            this.game,
            Scale.x(0.483),
            Scale.y(0.152),
            TtImage.SKULL,
            { group: this.smokelessParts },
        )
        this.skull.events.onInputDown.addOnce(() => {
            this.disableSmokelessParts()
            TobaccoTourActivityScreen.displayInteractiveScreen(
                this.game,
                TtImage.TOBACCO_BACKGROUND,
                this.molecules(),
                () => {
                    this.skull.grayOut()
                    this.completed = SmokelessTourStages.SKULL
                    this.enableSmokelessParts()
                },
                TtAudio.SKULL_AUDIO,
            )
        })

        this.chew = new ExpandoSprite(
            this.game,
            Scale.x(0.169),
            Scale.y(0.292),
            TtImage.CHEWING_TOBACCO,
            { group: this.smokelessParts },
        )
        this.chew.events.onInputDown.add(() => {
            this.disableSmokelessParts()
            const background = TobaccoTourActivityScreen.displayScreenBackground(
                this.game,
                this.game.add.group(),
                () =>
                    SmokelessTourChewAnimation.play(
                        this.game,
                        background,
                        this,
                    ),
                TtAtlas.CHEW_BACKGROUND,
            )
        })

        this.snuff = new ExpandoSprite(
            this.game,
            Scale.x(0.398),
            Scale.y(0.593),
            TtImage.SNUFF_CAN,
            { group: this.smokelessParts },
        )
        this.snuff.events.onInputDown.add(() => {
            this.disableSmokelessParts()
            const background = TobaccoTourActivityScreen.displayScreenBackground(
                this.game,
                this.game.add.group(),
                () =>
                    SmokelessTourSnuffAnimation.play(
                        this.game,
                        background,
                        this,
                    ),
                TtAtlas.SNUFF_BACKGROUND,
            )
        })

        Sprites.add(this.game, TtImage.MICROSCOPE_VIEW)

        if (this.stages.length === 0) {
            const audio1 = this.game.add.audio(TtAudio.SMOKELESS_1)
            const audio2 = this.game.add.audio(TtAudio.SMOKELESS_2)
            const debug = new DebugPanel(this.game, () => audio1.stop())
            audio1.onPlay.addOnce(this.disableSmokelessParts)
            audio1.onStop.addOnce(() => {
                audio2.play()
                debug.setSkip(() => audio2.stop())
            })
            audio2.onStop.addOnce(this.enableSmokelessParts)
            audio1.play()
        }
    }

    set completed(stage: SmokelessTourStages) {
        if (!this.stages.includes(stage)) {
            this.stages.push(stage)
        }

        if (this.stages.length === 3) {
            TobaccoTourProgressStore.instance.addStage(TobaccoTourStages.CHEW)
            this.game.state.start(ActivityKey.TOBACCO_TOUR)
        } else {
            const audio = this.game.add.audio(TtAudio.ANOTHER_OBJECT)
            audio.onStop.addOnce(this.enableSmokelessParts)
            audio.play()
        }
    }

    enableSmokelessParts = () => {
        this.smokelessParts.forEach((part: ExpandoSprite) => (part.on = true))
        PhaserUtils.enableGroup(this.smokelessParts)
    }

    disableSmokelessParts = () => {
        this.smokelessParts.forEach((part: ExpandoSprite) => (part.on = false))
        PhaserUtils.disableGroup(this.smokelessParts)
    }

    private molecules = (): Array<TobaccoTourMolecule> => [
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.POLONIUM,
            Scale.x(0.262),
            Scale.y(0.463),
            true,
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.FORMALDEHYDE,
            Scale.x(0.447),
            Scale.y(0.553),
            true,
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.NICOTINE,
            Scale.x(0.446),
            Scale.y(0.236),
            true,
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.NITROSAMINE,
            Scale.x(0.269),
            Scale.y(0.244),
            true,
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.ARSENIC,
            Scale.x(0.609),
            Scale.y(0.404),
            true,
        ),
    ]
}
